import {
  CheckboxItem,
  Content,
  Group,
  Item,
  ItemIndicator,
  Label,
  Portal,
  RadioGroup,
  RadioItem,
  Root,
  Separator,
  Sub,
  SubContent,
  SubTrigger,
  Trigger,
} from '@radix-ui/react-dropdown-menu';
import { ComponentProps, createContext, useContext, useState } from 'react';

import { mergeClasses } from '@/utils';

import { CheckIcon, ChevronRightIcon, CircleIcon, DotsVerticalIcon } from '@/assets/icons';
import { Button } from '@/components/common';
import { IDropdownMenuContextValue } from './DropdownMenu.interfaces';

const DropdownMenuContext = createContext<IDropdownMenuContextValue>({});

const DropdownMenu = ({ modal = false, open = false, ...props }: ComponentProps<typeof Root>) => {
  const [isOpen, setOpen] = useState(open);
  return (
    <DropdownMenuContext.Provider value={{ setOpen }}>
      <Root
        modal={modal}
        data-slot="dropdown-menu"
        open={isOpen}
        onOpenChange={setOpen}
        {...props}
      />
    </DropdownMenuContext.Provider>
  );
};

const DropdownMenuPortal = ({ ...props }: ComponentProps<typeof Portal>) => {
  return <Portal data-slot="dropdown-menu-portal" {...props} />;
};

const DropdownMenuTrigger = ({ children, ...props }: ComponentProps<typeof Trigger>) => {
  const { setOpen } = useContext(DropdownMenuContext);
  return (
    <Trigger
      onPointerDown={(e) => e.preventDefault()}
      onPointerUp={() => {
        setOpen?.(true);
      }}
      data-slot="dropdown-menu-trigger"
      asChild
      {...props}
    >
      {children ?? (
        <Button
          variant="secondary"
          borderRadius="square"
          className="border-none text-brand-primary-darkest hover:bg-neutral-n2 data-[state=open]:bg-neutral-n2 transition-colors focus:shadow-none focus-visible:outline-none"
          prefixIcon={<DotsVerticalIcon className="size-5" />}
        />
      )}
    </Trigger>
  );
};

const DropdownMenuContent = ({
  className,
  sideOffset = 4,
  ...props
}: ComponentProps<typeof Content>) => {
  return (
    <Portal>
      <Content
        data-slot="dropdown-menu-content"
        sideOffset={sideOffset}
        className={mergeClasses(
          'bg-neutral-white text-brand-primary-darkest font-medium text-body-sm [&_*:focus-visible]:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-h-(--radix-dropdown-menu-content-available-height) min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border p-1 shadow-md',
          className,
        )}
        {...props}
      />
    </Portal>
  );
};

const DropdownMenuGroup = ({ ...props }: ComponentProps<typeof Group>) => {
  return <Group data-slot="dropdown-menu-group" {...props} />;
};

const DropdownMenuItem = ({
  className,
  inset,
  variant = 'default',
  ...props
}: ComponentProps<typeof Item> & {
  inset?: boolean;
  variant?: 'default' | 'destructive';
}) => {
  return (
    <Item
      data-slot="dropdown-menu-item"
      data-inset={inset}
      data-variant={variant}
      className={mergeClasses(
        "cursor-pointer focus:bg-neutral-n2 px-base py-s data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 dark:data-[variant=destructive]:focus:bg-destructive/20 data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:!text-destructive [&_svg:not([class*='text-'])]:text-muted-foreground relative flex items-center gap-2 rounded-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
};

const DropdownMenuCheckboxItem = ({
  className,
  children,
  checked,
  ...props
}: ComponentProps<typeof CheckboxItem>) => {
  return (
    <CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      className={mergeClasses(
        "focus:bg-neutral-n2 relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      checked={checked}
      {...props}
    >
      <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
        <ItemIndicator>
          <CheckIcon className="size-4" />
        </ItemIndicator>
      </span>
      {children}
    </CheckboxItem>
  );
};

const DropdownMenuRadioGroup = ({ ...props }: ComponentProps<typeof RadioGroup>) => {
  return <RadioGroup data-slot="dropdown-menu-radio-group" {...props} />;
};

const DropdownMenuRadioItem = ({
  className,
  children,
  ...props
}: ComponentProps<typeof RadioItem>) => {
  return (
    <RadioItem
      data-slot="dropdown-menu-radio-item"
      className={mergeClasses(
        "focus:bg-neutral-n2 relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
        <ItemIndicator>
          <CircleIcon className="size-2 fill-current" />
        </ItemIndicator>
      </span>
      {children}
    </RadioItem>
  );
};

const DropdownMenuLabel = ({
  className,
  inset,
  ...props
}: ComponentProps<typeof Label> & {
  inset?: boolean;
}) => {
  return (
    <Label
      data-slot="dropdown-menu-label"
      data-inset={inset}
      className={mergeClasses('px-base py-s text-sm font-medium data-[inset]:pl-8', className)}
      {...props}
    />
  );
};

const DropdownMenuSeparator = ({ className, ...props }: ComponentProps<typeof Separator>) => {
  return (
    <Separator
      data-slot="dropdown-menu-separator"
      className={mergeClasses('bg-border -mx-1 my-1 h-px', className)}
      {...props}
    />
  );
};

const DropdownMenuShortcut = ({ className, ...props }: ComponentProps<'span'>) => {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={mergeClasses('text-muted-foreground ml-auto text-xs tracking-widest', className)}
      {...props}
    />
  );
};

const DropdownMenuSub = ({ ...props }: ComponentProps<typeof Sub>) => {
  return <Sub data-slot="dropdown-menu-sub" {...props} />;
};

const DropdownMenuSubTrigger = ({
  className,
  inset,
  children,
  ...props
}: ComponentProps<typeof SubTrigger> & {
  inset?: boolean;
}) => {
  return (
    <SubTrigger
      data-slot="dropdown-menu-sub-trigger"
      data-inset={inset}
      className={mergeClasses(
        "focus:bg-neutral-n2 data-[state=open]:bg-accent data-[state=open]:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground flex cursor-default items-center gap-2 rounded-sm px-base py-s text-sm outline-hidden select-none data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      {children}
      <ChevronRightIcon className="ml-auto size-4" />
    </SubTrigger>
  );
};

const DropdownMenuSubContent = ({ className, ...props }: ComponentProps<typeof SubContent>) => {
  return (
    <SubContent
      data-slot="dropdown-menu-sub-content"
      className={mergeClasses(
        'bg-neutral-white text-brand-primary-darkest font-medium text-body-sm [&_*:focus-visible]:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) overflow-hidden rounded-md border p-1 shadow-lg',
        className,
      )}
      {...props}
    />
  );
};

export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
};
